<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { useAccessControlAdminStore } from '@/stores/useAccessControlAdminStore'
import { useFeatureFlagAdminStore } from '@/stores/useFeatureFlagAdminStore'
import { useTemplateMailAdminStore } from '@/stores/useTemplateMailAdminStore'
import { usePortalPermissionAdminStore } from '@/stores/usePortalPermissionAdminStore'
import { useSourceAdminStore } from '@/stores/useSourceAdminStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import {
  UserGroupIcon,
  ShieldCheckIcon,
  FlagIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  MapIcon,
  BuildingStorefrontIcon,
  UserIcon,
  CircleStackIcon,
  TicketIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  MegaphoneIcon,
  RocketLaunchIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  SignalIcon,
  TruckIcon,
  ChartPieIcon,
  SquaresPlusIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  NewspaperIcon,
} from '@heroicons/vue/24/outline'

const accessStore = useAccessControlAdminStore()
const featureFlagStore = useFeatureFlagAdminStore()
const templateStore = useTemplateMailAdminStore()
const portalStore = usePortalPermissionAdminStore()
const sourceStore = useSourceAdminStore()
const permStore = useBoostCorePermissionStore()

const loading = ref(true)

interface AdminCard {
  title: string
  description: string
  icon: any
  to: string
  kpi?: () => string
  group: 'access' | 'organization' | 'commercial' | 'communication' | 'integrations' | 'support' | 'clientSpace' | 'platform'
  visible?: () => boolean
}

const cards: AdminCard[] = [
  {
    title: 'Utilisateurs',
    description: 'Gestion des comptes, sync Lucca/Odoo, sessions',
    icon: UserIcon,
    to: '/admin/users',
    group: 'access',
  },
  {
    title: 'Permissions & groupes',
    description: 'Permissions, composition des groupes, membres',
    icon: ShieldCheckIcon,
    to: '/dashboard/admin/access-control',
    kpi: () => `${accessStore.permissions.length} permission(s) · ${accessStore.roleGroups.length} groupe(s)`,
    group: 'access',
  },
  {
    title: 'Feature flags',
    description: 'Activation de fonctionnalités par utilisateur',
    icon: FlagIcon,
    to: '/dashboard/admin/featureflag',
    kpi: () => {
      const active = featureFlagStore.flags.filter((f) => f.status === 'ACTIVE').length
      return `${active} actif(s) sur ${featureFlagStore.flags.length}`
    },
    group: 'access',
  },
  {
    title: 'Portal permissions',
    description: "Permissions d'accès aux modules de l'espace client (éditorial + attribution)",
    icon: GlobeAltIcon,
    to: '/dashboard/admin/portal-permissions',
    kpi: () => `${portalStore.items.length} permission(s) enregistrée(s)`,
    group: 'clientSpace',
  },
  {
    title: 'Business Units',
    description: 'Préférences par BU (SENT_TO_BU_MANAGER, options métier…)',
    icon: BuildingStorefrontIcon,
    to: '/admin/business-units',
    group: 'organization',
    visible: () => permStore.isSuperAdmin,
  },
  {
    title: 'Régions',
    description: 'Découpage géographique',
    icon: MapIcon,
    to: '/admin/regions',
    group: 'organization',
  },
  {
    title: 'Équipes',
    description: 'Équipes par région',
    icon: UserGroupIcon,
    to: '/admin/teams',
    group: 'organization',
  },
  {
    title: 'Agences',
    description: 'Agences par équipe',
    icon: BuildingOfficeIcon,
    to: '/admin/agencies',
    group: 'organization',
  },
  {
    title: 'Suivi facturation devis',
    description: 'Devis signés, factures rattachées, statut facturé / payé. Filtres date, commercial, statut',
    icon: BanknotesIcon,
    to: '/dashboard/admin/quotes-billing',
    group: 'commercial',
    visible: () => permStore.can('SALES_READ'),
  },
  {
    title: 'Bandeaux d\'information',
    description: 'Messages défilants en haut de l\'application : texte, couleur, période, ciblage par droit ou BU',
    icon: MegaphoneIcon,
    to: '/dashboard/admin/banners',
    group: 'platform',
    visible: () => permStore.isSuperAdmin,
  },
  {
    title: 'News & changelog',
    description: 'Actualités poussées aux utilisateurs : rédaction markdown, publication immédiate ou programmée',
    icon: NewspaperIcon,
    to: '/dashboard/admin/news',
    group: 'platform',
    visible: () => permStore.isSuperAdmin,
  },
  {
    title: 'Templates Brevo',
    description: 'Mapping codes internes → IDs Brevo',
    icon: EnvelopeIcon,
    to: '/dashboard/admin/brevo-templates',
    kpi: () => `${templateStore.templates.length} template(s)`,
    group: 'communication',
  },
  {
    title: 'Livraisons',
    description: 'Suivi des pickings actifs, sync Odoo, mise à jour transporteur et tracking',
    icon: TruckIcon,
    to: '/dashboard/admin/deliveries',
    group: 'integrations',
    visible: () => permStore.can('DELIVERY_ADMIN') || permStore.isSuperAdmin,
  },
  {
    title: 'Sources',
    description: 'Connecteurs externes (Odoo, Alphalink, Keyyo, …) et entités liées',
    icon: CircleStackIcon,
    to: '/dashboard/admin/sources',
    kpi: () => `${sourceStore.sources.length} source(s)`,
    group: 'integrations',
  },
  {
    title: 'Imports CEGID',
    description: 'Suivi des synchros grand livre & statuts factures Odoo, relance et export des erreurs',
    icon: BanknotesIcon,
    to: '/admin/imports-cegid',
    group: 'integrations',
  },
  {
    title: 'Liens fibre',
    description: 'Monitoring des liens fibre via Thanos : état, débit, qualité',
    icon: SignalIcon,
    to: '/admin/fiber-links',
    group: 'integrations',
    visible: () => permStore.isSuperAdmin,
  },
  {
    title: 'Tickets DEV',
    description: 'Vue admin sur les tickets NinjaOne tag DEV : commentaires, statut, attribution',
    icon: TicketIcon,
    to: '/admin/tickets',
    group: 'support',
    visible: () => permStore.can('TICKETDEV_ADMIN'),
  },
  {
    title: 'Tickets DEV — stats',
    description: 'Indicateurs hebdo/mensuel, exports CSV/XLSX (réunion vendredi)',
    icon: ChartBarIcon,
    to: '/admin/tickets/stats',
    group: 'support',
    visible: () => permStore.can('TICKETDEV_ADMIN'),
  },
  {
    title: 'Demandes pôle dev',
    description: 'Référentiel interne des demandes (création, suivi, statut, GitHub)',
    icon: ClipboardDocumentListIcon,
    to: '/admin/pole-dev',
    group: 'support',
    visible: () => permStore.can('TICKETDEV_ADMIN'),
  },
  {
    title: 'Communications pôle dev',
    description: 'Préparer un envoi groupé des demandes livrées (template Brevo)',
    icon: MegaphoneIcon,
    to: '/admin/pole-dev/communications',
    group: 'support',
    visible: () => permStore.can('TICKETDEV_ADMIN'),
  },
  {
    title: 'Sprints pôle dev',
    description: 'Stats par sprint sur le project GitHub : taux complétion, charge par dev',
    icon: RocketLaunchIcon,
    to: '/admin/pole-dev/sprints',
    group: 'support',
    visible: () => permStore.can('TICKETDEV_ADMIN'),
  },
  {
    title: 'Points d\'attention',
    description: 'Blocages, sujets sensibles et transverses : suivi, interlocuteurs, levée',
    icon: ExclamationTriangleIcon,
    to: '/admin/pole-dev/points-attention',
    group: 'support',
    visible: () => permStore.can('TICKETDEV_ADMIN'),
  },
  {
    title: 'Espace client — Statistiques',
    description: 'Sociétés/contacts avec accès vs actifs, tickets créés depuis le portail',
    icon: ChartPieIcon,
    to: '/dashboard/admin/espace-client/stats',
    group: 'clientSpace',
    // Filtre groupe DEV_RESTRICTED_GROUPS suffit (pôle dev uniquement, sans bypass SUPER_ADMIN)
  },
  {
    title: 'Feature Registry',
    description: 'Référentiel fonctionnel : pages, capabilities, sync code → DB',
    icon: SquaresPlusIcon,
    to: '/dashboard/admin/espace-client/feature-registry',
    group: 'clientSpace',
  },
  {
    title: 'FAQ',
    description: 'Questions/réponses contextuelles affichées dans l\'espace client',
    icon: QuestionMarkCircleIcon,
    to: '/dashboard/admin/espace-client/faq',
    group: 'clientSpace',
  },
  {
    title: 'Pages légales',
    description: 'Mentions légales, confidentialité, cookies, CGU, sécurité (footer du portail)',
    icon: DocumentTextIcon,
    to: '/dashboard/admin/espace-client/legal-pages',
    group: 'clientSpace',
  },
]

const groupLabels: Record<AdminCard['group'], string> = {
  access: 'Utilisateurs & accès',
  organization: 'Organisation',
  commercial: 'Commercial',
  platform: 'Plateforme',
  communication: 'Communication',
  integrations: 'Intégrations',
  support: 'Support',
  clientSpace: 'Espace client',
}

const groupedCards = ref<Record<AdminCard['group'], AdminCard[]>>({
  access: [],
  organization: [],
  commercial: [],
  platform: [],
  communication: [],
  integrations: [],
  support: [],
  clientSpace: [],
})

onMounted(async () => {
  for (const c of cards) {
    if (c.visible && !c.visible()) continue
    groupedCards.value[c.group].push(c)
  }
  // Pre-load des KPIs en parallèle. Si une permission est manquante,
  // l'appel échoue silencieusement et la card s'affiche sans compteur.
  await Promise.allSettled([
    accessStore.fetchPermissions(),
    accessStore.fetchRoleGroups(),
    featureFlagStore.fetchFlags(),
    templateStore.fetchAll(),
    portalStore.fetchAll(),
    sourceStore.fetchAll(),
  ])
  loading.value = false
})

type GroupKey = AdminCard['group']

// Groupes strictement réservés au pôle dev (DEV_ADMIN ou TICKETDEV_ADMIN exactement).
// On utilise `include()` (pas `can()`) pour qu'un SUPER_ADMIN ne passe PAS via bypass :
// seul un user du pôle dev voit ces features.
const DEV_RESTRICTED_GROUPS: GroupKey[] = ['communication', 'integrations', 'support', 'clientSpace']
const isPoleDev = computed(() =>
  permStore.include('DEV_ADMIN') || permStore.include('TICKETDEV_ADMIN'),
)

const visibleGroups = computed<Array<{ key: GroupKey; label: string }>>(() =>
  (Object.entries(groupLabels) as Array<[GroupKey, string]>)
    .filter(([key]) => {
      if (DEV_RESTRICTED_GROUPS.includes(key) && !isPoleDev.value) return false
      return (groupedCards.value[key] ?? []).length > 0
    })
    .map(([key, label]) => ({ key, label })),
)
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Administration" subtitle="Données référentielles" />

    <div v-for="group in visibleGroups" :key="group.key" class="mt-8">
      <h2 class="text-lg font-semibold mb-3 text-base-content/80">{{ group.label }}</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <router-link
          v-for="card in groupedCards[group.key]"
          :key="card.to"
          :to="card.to"
          class="card bg-base-100 shadow hover:shadow-lg transition-shadow cursor-pointer border border-transparent hover:border-primary"
        >
          <div class="card-body">
            <div class="flex items-start gap-3">
              <component :is="card.icon" class="w-8 h-8 text-primary shrink-0" />
              <div class="flex-1">
                <h3 class="font-semibold">{{ card.title }}</h3>
                <p class="text-sm text-base-content/60 mt-1">{{ card.description }}</p>
                <div v-if="card.kpi" class="mt-3 text-sm">
                  <span v-if="loading" class="text-base-content/40">Chargement…</span>
                  <span v-else class="badge badge-ghost">{{ card.kpi() }}</span>
                </div>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>
